import { Star } from 'lucide-react';
import { cn } from '@/lib/cn';

/**
 * AverageRatingBadge — compact "4.3 ★ (27)" pill used in the book detail
 * header and (optionally) on book cards. Renders nothing when the book
 * has no ratings yet, so callers can drop it in unconditionally.
 *
 * Two size presets:
 *   - `sm`: card-overlay sized (no parens around count to save room).
 *   - `md`: book-detail-header sized.
 *
 * Always paints in or-orange-500 to read as "rating" rather than "tag".
 */
export interface AverageRatingBadgeProps {
  averageRating: number | null;
  ratingCount: number;
  size?: 'sm' | 'md';
  className?: string;
}

export function AverageRatingBadge({
  averageRating,
  ratingCount,
  size = 'md',
  className,
}: AverageRatingBadgeProps) {
  if (averageRating === null || ratingCount === 0) return null;

  const isSm = size === 'sm';
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-full bg-or-orange-50 px-2 font-semibold text-or-orange-700',
        isSm ? 'py-0.5 text-[11px]' : 'py-1 text-[13px]',
        className,
      )}
      aria-label={`Average rating ${averageRating.toFixed(1)} out of 5, from ${ratingCount} ${
        ratingCount === 1 ? 'reader' : 'readers'
      }`}
    >
      <Star
        className={cn(isSm ? 'h-3 w-3' : 'h-3.5 w-3.5', 'fill-or-orange-500 text-or-orange-500')}
        aria-hidden
      />
      <span>{averageRating.toFixed(1)}</span>
      <span className={cn('font-normal text-or-orange-600', isSm ? 'opacity-80' : '')}>
        {isSm ? `(${ratingCount})` : `· ${ratingCount} ${ratingCount === 1 ? 'rating' : 'ratings'}`}
      </span>
    </span>
  );
}
